import { useEffect, useState } from "react";
import { useNavigate } from "react-router";
import Toggle from "@/app/components/ui/Toggles";
import { useRouter } from "@/app/hooks/useRouter";
import { PencilSquareIcon } from "@heroicons/react/24/outline";
import { PRIMARY_BUTTON } from "@/constants";
import clsx from "clsx";
import { useGroupItemsListAPI } from "@/app/hooks/api_hooks/Inventory/GroupItems/useGroupItemsListAPI";
import NoDataFoundComponent from "@/app/components/ui/NoRecordFound";
import { useImageConstants } from "@/app/hooks/useImageConstants";
import InputWithAddOn from "@/app/components/ui/Inputs/InputWithAddOn";
import { FaProductHunt } from "react-icons/fa6";
import PaginationComponent from "@/app/components/ui/Pagination";
import { useLoader } from "@/app/hooks/useLoader";
import TableSkeleton from "@/app/components/ui/Skeletons/TableSkeleton";
import { IInventoryState } from "@/app_redux/reducers/slices/auth/inventory_slice";
import useRouteInfo from "@/app/hooks/useRouteInfo";
import { Link } from "react-router-dom";

const PAGE_SIZE = 10;

const tableHeader = [{ item: "Parent Item" }, { item: "Child Item(s)" }, { item: "Status" }, { item: "Action" }];

const ItemGroupSetupListing = () => {
  const navigate = useNavigate();
  const [isExpanded, setIsExpanded] = useState(false);
  const toggleViewMore = () => setIsExpanded((prev) => !prev);
  const { execute: groupItemList } = useGroupItemsListAPI();
  const [searchQuery, setSearchQuery] = useState<string>("");
  const [currentPage, setCurrentPage] = useState<number>(1);
  const { NO_PWL_RESULTS } = useImageConstants();
  const { getRouteKey } = useRouter();
  const routeId = getRouteKey("INVENTORY", "id");
  const { routeState } = useRouteInfo(routeId);
  const { groupItemsList } = routeState.state as IInventoryState;
  const isProductListInProgess = useLoader("isInventoryProductListLoadingInProgess");

  useEffect(() => {
    groupItemList(currentPage, PAGE_SIZE, searchQuery);
  }, [currentPage, searchQuery]);

  const results = groupItemsList?.results ?? [];

  return (
    <div className="px-4 sm:px-6 lg:px-8 mt-10">
      <div className="flex flex-row items-center justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-sm sm:text-base font-semibold leading-6 text-gray-900 whitespace-nowrap">
            Listed Item Groups
          </h1>
          <p className="mt-2 text-sm text-gray-700 hidden sm:block">A list of all the item groups for the inventories.</p>
        </div>
        <Link to={getRouteKey("ITEM_GROUP_ONBOARD", "url")} className={clsx(PRIMARY_BUTTON, "shrink-0")}>
          Add Item Group
        </Link>
      </div>
      <div className="mt-3 hidden sm:block">
        <InputWithAddOn
          onEnterKeyPress={(query: string) => {
            setSearchQuery(query);
            setCurrentPage(1);
          }}
          containerClassName="w-full sm:w-1/2 lg:w-1/3"
          addon={<FaProductHunt className="w-4 h-4" />}
          placeholder="Search Item Group"
        />
      </div>
      <input
        type="text"
        value={searchQuery}
        onChange={(e) => {
          setSearchQuery(e.target.value);
          setCurrentPage(1);
        }}
        className="p-1 rounded-lg sm:hidden mt-2 w-full border"
        placeholder="Search Item Group"
      />
      <div className="mt-8 flow-root">
        {isProductListInProgess ? (
          <TableSkeleton />
        ) : results.length === 0 ? (
          <NoDataFoundComponent
            imageURL={NO_PWL_RESULTS}
            message="No Product Listed"
            description="No Product Listed, please onboard some products."
          />
        ) : (
          <>
            <div className="outer-container relative rounded-xl bg-[#F9FAFB]">
              <div className="scroll-inner !h-auto max-h-[600px]">
                <div className="w-full">
                  <div className="inline-block min-w-full align-middle">
                    <div className="bg-white rounded-lg">
                      <table className="w-full min-w-[640px] border-separate border-spacing-x-0 border-spacing-y-0.5 bg-[#F5F5F5] rounded-xl px-[3px] pb-[1px]">
                        <thead className="bg-[#F5F5F5] text-[#1F1F23] top-0 font-normal">
                          <tr>
                            {tableHeader.map((key, index) => (
                              <th
                                key={index}
                                scope="col"
                                className="px-3 py-3 text-left whitespace-nowrap text-xs font-medium text-[#1F1F23] capitalize"
                              >
                                {key?.item}
                              </th>
                            ))}
                          </tr>
                        </thead>
                        <tbody className="overflow-hidden rounded-xl w-full">
                          {results.map((item, idx) => {
                            const isFirst = idx === 0;
                            const isLast = idx === results.length - 1;
                            const isSingle = results.length === 1;

                            return (
                              <tr
                                key={item?.item_group_id}
                                className={clsx(
                                  "group bg-white transition",
                                  isSingle && "rounded-xl",
                                  isFirst && !isSingle && "rounded-t-xl",
                                  isLast && !isSingle && "rounded-b-xl",
                                )}
                              >
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal",
                                    isSingle && "rounded-tl-2xl rounded-bl-2xl",
                                    isFirst && !isSingle && "rounded-tl-2xl",
                                    isLast && !isSingle && "rounded-bl-2xl",
                                  )}
                                >
                                  {item?.parent_item_name}
                                </td>
                                <td className="px-3 py-3 text-xs font-normal">
                                  {isExpanded ? (
                                    <div className="space-y-1">
                                      {item?.child_item_names.map((name, index) => (
                                        <p key={index}>{name}</p>
                                      ))}
                                    </div>
                                  ) : (
                                    item?.child_item_names.slice(0, 2).map((child, index) => (
                                      <p key={index}>{child}</p>
                                    ))
                                  )}
                                  {item?.child_item_names.length > 2 && (
                                    <p
                                      className="font-medium text-[#0267fe] text-xs cursor-pointer mt-2"
                                      onClick={toggleViewMore}
                                    >
                                      {isExpanded ? "View Less" : "View More"}
                                    </p>
                                  )}
                                </td>
                                <td className="px-3 py-3 whitespace-nowrap text-xs font-normal">
                                  <Toggle onToggle={() => {}} checked={item?.status === "active"} disabled />
                                </td>
                                <td
                                  className={clsx(
                                    "px-3 py-3 whitespace-nowrap text-xs font-normal cursor-pointer",
                                    isSingle && "rounded-tr-2xl rounded-br-2xl",
                                    isFirst && !isSingle && "rounded-tr-2xl",
                                    isLast && !isSingle && "rounded-br-2xl",
                                  )}
                                  onClick={() => navigate(`/app/inventory/item-group-onboard/${item?.item_group_id}`)}
                                >
                                  <PencilSquareIcon className="w-4 h-4" />
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <div className="flex justify-center items-center p-4 w-full">
              <PaginationComponent
                className="pagination-bar"
                currentPage={currentPage}
                pageSize={PAGE_SIZE}
                totalCount={groupItemsList?.total_records || 0}
                onPageChange={(pageNumber) => setCurrentPage(pageNumber)}
              />
            </div>
          </>
        )}
      </div>
    </div>
  );
};
export default ItemGroupSetupListing;
